/**
 * FeaturesDialog - Quick-access grid for toggling calendar features.
 *
 * Modeled after ImagePresetsDialog: full dialog with search bar and
 * responsive card grid, each card showing a feature toggle.
 */

import { useState, useMemo } from 'react';
import {
  Sun,
  Sunset,
  Target,
  ListTodo,
  Cloud,
  Link,
  Users,
  AlertTriangle,
  Clock,
  Calendar,
  Hash,
  BarChart3,
  Upload,
  Search,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import type { CalendarSettings } from '../types';

interface FeatureItem {
  key: keyof CalendarSettings;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const FEATURES: FeatureItem[] = [
  {
    key: 'dailyPlanningEnabled',
    name: 'Plan My Day',
    description: 'Morning planning wizard to organize your day',
    icon: Sun,
  },
  {
    key: 'eveningReviewEnabled',
    name: 'Evening Review',
    description: 'End-of-day review and reflection wizard',
    icon: Sunset,
  },
  {
    key: 'showToolbarFocusTime',
    name: 'Focus Time',
    description: 'Block distraction-free time on your calendar',
    icon: Target,
  },
  {
    key: 'showToolbarTasks',
    name: 'Tasks',
    description: 'Task panel for to-dos and scheduling',
    icon: ListTodo,
  },
  {
    key: 'showWeather',
    name: 'Weather',
    description: 'Show weather forecast on week and day views',
    icon: Cloud,
  },
  {
    key: 'showToolbarSchedulingLinks',
    name: 'Scheduling Links',
    description: 'Share booking links for easy scheduling',
    icon: Link,
  },
  {
    key: 'showToolbarFindTime',
    name: 'Find a Time',
    description: 'Find available meeting slots across calendars',
    icon: Users,
  },
  {
    key: 'showConflictWarnings',
    name: 'Conflict Warnings',
    description: 'Warn when events overlap or conflict',
    icon: AlertTriangle,
  },
  {
    key: 'dimPastEvents',
    name: 'Dim Past Events',
    description: 'Reduce opacity of past events',
    icon: Clock,
  },
  {
    key: 'showWeekends',
    name: 'Show Weekends',
    description: 'Display Saturday and Sunday columns',
    icon: Calendar,
  },
  {
    key: 'showWeekNumbers',
    name: 'Week Numbers',
    description: 'Show ISO week numbers in the calendar',
    icon: Hash,
  },
  {
    key: 'showToolbarAnalytics',
    name: 'Analytics',
    description: 'Calendar usage analytics and insights',
    icon: BarChart3,
  },
  {
    key: 'showToolbarImport',
    name: 'Import',
    description: 'Import events from ICS files',
    icon: Upload,
  },
];

interface FeaturesDialogProps {
  open: boolean;
  onClose: () => void;
  settings: CalendarSettings;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
}

export function FeaturesDialog({ open, onClose, settings, onUpdateSettings }: FeaturesDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFeatures = useMemo(() => {
    if (!searchQuery.trim()) return FEATURES;
    const query = searchQuery.toLowerCase();
    return FEATURES.filter(
      (f) => f.name.toLowerCase().includes(query) || f.description.toLowerCase().includes(query),
    );
  }, [searchQuery]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Features</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          You can use Fias AI to enable or configure any calendar feature. For more in-depth
          settings and customization, visit the Settings tab.
        </p>

        <div className="relative mt-2">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search features..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {filteredFeatures.map((feature) => {
            const Icon = feature.icon;
            const checked = Boolean(settings[feature.key]);
            return (
              <div key={feature.key} className="flex items-start gap-3 rounded-lg border p-3">
                <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium leading-none">{feature.name}</span>
                    <Switch
                      checked={checked}
                      onCheckedChange={(v) =>
                        onUpdateSettings({ [feature.key]: v } as Partial<CalendarSettings>)
                      }
                      className="shrink-0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {filteredFeatures.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">
            No features match your search.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
